import { Container, Graphics, Text } from "pixi.js"
import { Entity, EntityId } from "game-common/models"
import { EMBER_PORTRAIT_ZOOM, LPC_PORTRAIT_HEAD, Paperdoll, paperdollFor } from "../sprites/ember_portrait"
import { LpcLayered } from "../sprites/lpc_layered"
import { TextRenderer } from "../text/text_renderer"
import { DAMAGE_GHOST, DAMAGE_NUMBER_COLOR, DAMAGE_RING, DAMAGE_WASH, hpColor } from "./ember_roster_card"
import { DamageFrame } from "./roster_damage"

// The player's card at the head of their own pack: an ember roster card grown to fit the space between
// the gear sockets. The same zoomed portrait filling the frame like a background, the name and the
// green/yellow/red HP bar on a scrim at its foot, and the same hit (a red wash, a ring, the pale
// "health just lost" segment, a rising number) -- the world stays live behind the pack, so the player
// can be hit while it is open.
//
// Framed looser than a roster card -- head to thigh rather than head and shoulders -- because this one
// is also a mirror: the armour, the shield and what the hands hold are all worth seeing here.

export const PROFILE_CARD_W = 192
export const PROFILE_CARD_H = 162
const INSET = 5
const RADIUS = 7
// Frame pixels to card pixels: 3, against a roster card's 2.85 (PORTRAIT_SCALE 1.5 x the Ember zoom).
const ZOOM = 3
const PORTRAIT_SCALE = ZOOM / EMBER_PORTRAIT_ZOOM
// Where the head sits on the card.
const HEAD = { x: PROFILE_CARD_W / 2, y: 48 }
const SCRIM_H = 40
const HP_MARGIN = 9
const HP_H = 6
const HP_Y = PROFILE_CARD_H - INSET - 8 - HP_H
const LABEL_BOTTOM = HP_Y - 4
const NUMBER_Y = 58

export class PackProfileCard extends Container {
    private readonly art = new Container()
    private readonly portrait: Paperdoll
    private readonly hpBar = new Graphics()
    private readonly hpText: Text
    private readonly wash = new Graphics()
    private readonly ring = new Graphics()
    private damageNumber?: Text
    // What was last drawn, so a frame at rest costs nothing.
    private drawn?: string

    constructor(entity: Entity, entityId: EntityId, name: string | undefined) {
        super()
        this.sortableChildren = false

        const chrome = new Graphics()
        chrome.beginFill(0x0a0d08, 0.92)
        chrome.lineStyle(1, 0xffffff, 0.1)
        chrome.drawRoundedRect(0.5, 0.5, PROFILE_CARD_W - 1, PROFILE_CARD_H - 1, RADIUS)
        chrome.endFill()
        this.addChild(chrome)

        // The portrait and the firelight behind it, clipped to the inset by a plain rect: pixi clips a
        // rect with a scissor box, where a rounded mask would be a stencil pass every frame (the roster's
        // fps work measured those). The corners it lets through are the dark backdrop either way.
        const clip = new Graphics()
        clip.beginFill(0xffffff)
        clip.drawRect(INSET, INSET, PROFILE_CARD_W - INSET * 2, PROFILE_CARD_H - INSET * 2)
        clip.endFill()
        this.addChild(clip)
        this.art.mask = clip
        this.addChild(this.art)

        const backdrop = new Graphics()
        backdrop.beginFill(0x0d0b08, 1)
        backdrop.drawRoundedRect(INSET, INSET, PROFILE_CARD_W - INSET * 2, PROFILE_CARD_H - INSET * 2, RADIUS - 1)
        backdrop.endFill()
        // Warmed from below, as if standing at the fire -- the pack's old portrait socket did the same.
        for (let ring = 0; ring < 6; ring++) {
            backdrop.beginFill(ring < 2 ? 0x5a3018 : 0x8a4220, 0.08)
            backdrop.drawEllipse(PROFILE_CARD_W / 2, PROFILE_CARD_H - INSET, PROFILE_CARD_W * (0.62 - ring * 0.09), 70 - ring * 10)
            backdrop.endFill()
        }
        this.art.addChild(backdrop)

        try {
            this.portrait = paperdollFor(entity, entityId, true, "head")
        } catch (e) {
            // Malformed appearance data must not take the pack down with it -- leave the card empty.
            console.warn(`[PackProfileCard] portrait build failed for ${entityId}`, e)
            this.portrait = new LpcLayered(entityId, true)
        }
        this.portrait.scale.set(PORTRAIT_SCALE)
        this.portrait.position.set(HEAD.x - LPC_PORTRAIT_HEAD.x * PORTRAIT_SCALE, HEAD.y - LPC_PORTRAIT_HEAD.y * PORTRAIT_SCALE)
        this.art.addChild(this.portrait)

        const scrim = new Graphics()
        scrim.beginFill(0x000000, 0.5)
        scrim.drawRoundedRect(INSET, PROFILE_CARD_H - INSET - SCRIM_H, PROFILE_CARD_W - INSET * 2, SCRIM_H, RADIUS - 1)
        scrim.endFill()
        this.addChild(scrim)

        const nameText = new TextRenderer().role("compactBody").bold(true).color(0xffffff).render(name || "") as Text
        nameText.x = INSET + 5
        nameText.y = Math.round(LABEL_BOTTOM - nameText.height)
        this.addChild(nameText)

        this.hpText = new TextRenderer().role("compactCaption").bold(true).color(0xffffff).render("") as Text
        this.hpText.alpha = 0.8
        this.addChild(this.hpText)

        this.hpBar.position.set(HP_MARGIN, HP_Y)
        this.addChild(this.hpBar)

        this.wash.beginFill(DAMAGE_WASH, 1)
        this.wash.drawRoundedRect(INSET, INSET, PROFILE_CARD_W - INSET * 2, PROFILE_CARD_H - INSET * 2, RADIUS - 1)
        this.wash.endFill()
        this.wash.alpha = 0
        this.addChild(this.wash)

        this.ring.lineStyle(2, DAMAGE_RING, 1)
        this.ring.drawRoundedRect(1, 1, PROFILE_CARD_W - 2, PROFILE_CARD_H - 2, RADIUS)
        this.ring.alpha = 0
        this.addChild(this.ring)
    }

    /** Draw the HP and whatever of a hit is still playing. Cheap when nothing has changed. */
    paint = (hp: number | undefined, hpMax: number | undefined, frame: DamageFrame | null) => {
        const key = `${Math.ceil(hp ?? 0)}/${hpMax ?? 0}/${frame ? frame.seq + ":" + frame.ghostRatio.toFixed(3) + ":" + frame.numberAlpha.toFixed(2) : ""}`
        if (key !== this.drawn) {
            this.drawn = key
            this.drawHp(hp, hpMax, frame?.ghostRatio)
            this.hpText.text = hpMax ? `${Math.ceil(Math.max(0, hp ?? 0))} / ${Math.ceil(hpMax)}` : ""
            this.hpText.x = Math.round(PROFILE_CARD_W - INSET - 5 - this.hpText.width)
            this.hpText.y = Math.round(LABEL_BOTTOM - this.hpText.height)
        }
        this.wash.alpha = frame?.flashAlpha ?? 0
        this.ring.alpha = frame?.ringAlpha ?? 0
        this.art.position.set(frame?.shakeX ?? 0, frame?.shakeY ?? 0)
        if (frame && frame.numberAlpha > 0) {
            const number = this.ensureDamageNumber()
            number.text = `-${frame.amount}`
            number.alpha = frame.numberAlpha
            number.x = Math.round(PROFILE_CARD_W / 2 - number.width / 2)
            number.y = Math.round(NUMBER_Y - frame.numberRise)
        } else if (this.damageNumber) {
            this.damageNumber.alpha = 0
        }
    }

    // The roster's bar, wider: a dark groove, the pale segment just lost (under the live fill, so only
    // the difference shows), and the fill in the roster's green/yellow/red.
    private drawHp = (hp: number | undefined, hpMax: number | undefined, ghostRatio?: number) => {
        const bar = this.hpBar
        const w = PROFILE_CARD_W - HP_MARGIN * 2
        bar.clear()
        bar.beginFill(0x000000, 0.6)
        bar.drawRoundedRect(0, 0, w, HP_H, 2)
        bar.endFill()
        if (ghostRatio !== undefined && ghostRatio > 0) {
            bar.beginFill(DAMAGE_GHOST, 0.9)
            bar.drawRoundedRect(0, 0, Math.max(1, w * Math.min(1, ghostRatio)), HP_H, 2)
            bar.endFill()
        }
        if (hp !== undefined && hpMax && hpMax > 0) {
            const ratio = Math.max(0, Math.min(1, hp / hpMax))
            if (ratio > 0) {
                bar.beginFill(hpColor(ratio), 0.95)
                bar.drawRoundedRect(0, 0, Math.max(1, w * ratio), HP_H, 2)
                bar.endFill()
            }
        }
    }

    private ensureDamageNumber = (): Text => {
        if (!this.damageNumber) {
            this.damageNumber = new TextRenderer().role("title").color(DAMAGE_NUMBER_COLOR).render("") as Text
            this.addChild(this.damageNumber)
        }
        return this.damageNumber
    }
}
